import React, { useState } from 'react';
import { 
  PenTool, 
  ArrowLeft, 
  ShieldCheck, 
  CheckCircle2, 
  Clock, 
  FileText, 
  User, 
  DollarSign, 
  Lock, 
  ChevronRight, 
  Plus, 
  Share2, 
  Search,
  Filter,
  Layers,
  AlertTriangle
} from 'lucide-react';
import { formatCurrencyBRL } from '../../../utils/formatUtils';
import TouchSignatureModal from './TouchSignatureModal';
import ScreenTourButton from '../../../components/tour/ScreenTourButton';
import { DigitalSignature, DocumentType, Client, WorkOrder } from '../types';
import { Quote } from '../../../types';

export interface SignableDocument {
  id: string;
  code: string; // Ex: OS-1042, ORC-882
  type: DocumentType;
  title: string;
  clientName: string;
  clientDocument?: string;
  clientPhone?: string;
  totalValue: number;
  status: 'pending' | 'signed';
  signature?: DigitalSignature;
  createdAt: string;
}

interface SignatureDocumentCenterProps {
  providerName: string;
  clients: Client[];
  quotes?: Quote[];
  workOrders?: WorkOrder[];
  onSaveSignature?: (signature: DigitalSignature) => Promise<void> | void;
  onSaveWorkOrder?: (workOrder: WorkOrder) => Promise<void> | void;
  onBack: () => void;
}

export default function SignatureDocumentCenter({
  providerName,
  clients,
  quotes = [],
  workOrders = [],
  onSaveSignature,
  onSaveWorkOrder,
  onBack
}: SignatureDocumentCenterProps) {
  const [activeFilter, setActiveFilter] = useState<'all' | 'pending' | 'signed'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [customTerms, setCustomTerms] = useState<SignableDocument[]>([]);
  const [viewingSignedDoc, setViewingSignedDoc] = useState<SignableDocument | null>(null);

  // Montagem dinâmica a partir das propostas e ordens de serviço reais do usuário
  const realDocuments: SignableDocument[] = [
    ...workOrders.map(wo => {
      const hasSignature = Boolean(wo.signatures && wo.signatures.length > 0);
      const latestSig = hasSignature && wo.signatures ? wo.signatures[wo.signatures.length - 1] : undefined;
      return {
        id: wo.id,
        code: `OS #${wo.orderNumber || wo.id.substring(0, 6).toUpperCase()}`,
        type: 'work_order_delivery' as DocumentType,
        title: wo.title || 'Ordem de Serviço',
        clientName: wo.clientName || 'Cliente',
        clientDocument: wo.clientDocument,
        clientPhone: wo.clientPhone,
        totalValue: wo.finalPrice || 0,
        status: (hasSignature || wo.status === 'completed' ? 'signed' : 'pending') as 'pending' | 'signed',
        signature: latestSig,
        createdAt: wo.createdAt ? new Date(wo.createdAt).toLocaleDateString('pt-BR') : 'Recente'
      };
    }),
    ...quotes.map(q => ({
      id: q.id,
      code: `ORC #${q.id.substring(0, 6).toUpperCase()}`,
      type: 'quote_approval' as DocumentType,
      title: q.rawServiceName || 'Proposta de Serviço',
      clientName: q.clientName || 'Cliente',
      clientDocument: undefined,
      clientPhone: q.clientPhone,
      totalValue: q.finalPrice || q.finalSuggestedPrice || 0,
      status: (q.status === 'approved' ? 'signed' : 'pending') as 'pending' | 'signed',
      createdAt: q.createdAt ? new Date(q.createdAt).toLocaleDateString('pt-BR') : 'Recente'
    })),
    ...customTerms
  ];

  // Estados do Modal de Assinatura Ativa
  const [selectedDocForSign, setSelectedDocForSign] = useState<SignableDocument | null>(null);
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);

  // Estados do Modal de Termo Avulso Rápido
  const [isQuickTermModalOpen, setIsQuickTermModalOpen] = useState(false);
  const [quickTitle, setQuickTitle] = useState('Termo de Aceite e Entrega de Serviços');
  const [quickClientName, setQuickClientName] = useState(clients[0]?.name || 'Novo Cliente');
  const [quickValue, setQuickValue] = useState<number>(350);

  const handleOpenSign = (doc: SignableDocument) => {
    setSelectedDocForSign(doc);
    setIsSignModalOpen(true);
  };

  const handleSaveSignature = async (sig: DigitalSignature) => {
    if (!selectedDocForSign) return;

    if (onSaveSignature) {
      await onSaveSignature(sig);
    }

    // Se o documento assinado for uma Ordem de Serviço, salva a assinatura nela
    if (selectedDocForSign.type === 'work_order_delivery' && onSaveWorkOrder) {
      const targetWo = workOrders.find(w => w.id === selectedDocForSign.id);
      if (targetWo) {
        const updatedSignatures = targetWo.signatures ? [...targetWo.signatures, sig] : [sig];
        await onSaveWorkOrder({
          ...targetWo,
          signatures: updatedSignatures,
          status: 'completed',
          updatedAt: new Date().toISOString()
        });
      }
    }

    setCustomTerms(prev => prev.map(d => {
      if (d.id === selectedDocForSign.id) {
        return {
          ...d,
          status: 'signed',
          signature: sig
        };
      }
      return d;
    }));

    setIsSignModalOpen(false);
    setSelectedDocForSign(null);
    alert(`Assinatura registrada com sucesso para ${selectedDocForSign.code}! Registro de segurança gravado.`);
  };

  const handleCreateQuickTerm = (e: React.FormEvent) => {
    e.preventDefault();
    const newDoc: SignableDocument = {
      id: `doc_${Date.now()}`,
      code: `TERMO #${Math.floor(1000 + Math.random() * 9000)}`,
      type: 'consent_term',
      title: quickTitle,
      clientName: quickClientName,
      totalValue: quickValue,
      status: 'pending',
      createdAt: 'Agora'
    };

    setCustomTerms([newDoc, ...customTerms]);
    setIsQuickTermModalOpen(false);
    handleOpenSign(newDoc);
  };

  const filteredDocs = realDocuments.filter(doc => {
    const matchesFilter = activeFilter === 'all' || doc.status === activeFilter;
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          doc.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          doc.code.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const pendingCount = realDocuments.filter(d => d.status === 'pending').length;
  const signedCount = realDocuments.filter(d => d.status === 'signed').length;

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 pb-20 font-sans">
      {/* Header Slim (56px) — Padrão R-MAX */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-zinc-200/80 px-4 sm:px-6 h-14 flex items-center shadow-2xs">
        <div className="max-w-5xl mx-auto w-full flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <button
              type="button"
              onClick={onBack}
              className="p-2 -ml-1 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-xl transition-all active:scale-95 cursor-pointer shrink-0"
              title="Voltar"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-sm sm:text-base font-black text-zinc-900 truncate tracking-tight">
                Assinatura do Cliente
              </h1>
              <p className="text-[11px] text-zinc-500 truncate font-medium">
                {pendingCount > 0
                  ? `${pendingCount} documento${pendingCount > 1 ? 's' : ''} aguardando assinatura`
                  : `${signedCount} documento${signedCount !== 1 ? 's' : ''} assinado${signedCount !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <ScreenTourButton screenKey="signatures" />
            <button
              type="button"
              onClick={() => setIsQuickTermModalOpen(true)}
              className="px-3 sm:px-4 py-2 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-sm shadow-brand/20 flex items-center gap-1.5 cursor-pointer active:scale-95 transition-all shrink-0"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Novo Termo</span>
              <span className="sm:hidden">+ Novo</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 space-y-5">

        {/* Banner de Instrução — Compacto e Amigável */}
        <div className="bg-zinc-900 text-white px-5 py-4 rounded-3xl flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-brand/20 border border-brand/30 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5 text-emerald-400" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-black text-emerald-400 uppercase tracking-widest mb-0.5">Como funciona</p>
            <p className="text-sm font-bold text-white leading-snug">
              Peça para o cliente assinar na tela do celular e guarde o comprovante com data e hora. 📱✅
            </p>
          </div>
        </div>

        {/* Filtros + Busca */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex bg-zinc-100 p-1 rounded-2xl text-xs font-bold">
            <button
              type="button"
              onClick={() => setActiveFilter('all')}
              className={`px-3 sm:px-4 py-2 rounded-xl transition-all cursor-pointer flex items-center gap-1.5 ${
                activeFilter === 'all' ? 'bg-white text-zinc-900 shadow-xs font-black' : 'text-zinc-600 hover:text-zinc-900'
              }`}
            >
              <Layers className="h-3.5 w-3.5" />
              Todos ({realDocuments.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter('pending')}
              className={`px-3 sm:px-4 py-2 rounded-xl transition-all cursor-pointer flex items-center gap-1.5 ${
                activeFilter === 'pending' ? 'bg-amber-500 text-white shadow-xs font-black' : 'text-zinc-600 hover:text-zinc-900'
              }`}
            >
              <Clock className="h-3.5 w-3.5" />
              Pendentes ({pendingCount})
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter('signed')}
              className={`px-3 sm:px-4 py-2 rounded-xl transition-all cursor-pointer flex items-center gap-1.5 ${
                activeFilter === 'signed' ? 'bg-brand text-white shadow-xs font-black' : 'text-zinc-600 hover:text-zinc-900'
              }`}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Assinados ({signedCount})
            </button>
          </div>

          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <input
              type="text"
              placeholder="Buscar por cliente ou serviço..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full bg-white border border-zinc-200/80 rounded-2xl pl-10 pr-4 py-2.5 text-sm text-zinc-900 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20 shadow-2xs"
            />
          </div>
        </div>

        {/* Lista dos Documentos */}
        <div className="space-y-3">
          {filteredDocs.length === 0 ? (
            <div className="bg-white p-10 rounded-3xl border border-zinc-100 text-center space-y-2">
              <FileText className="h-10 w-10 text-zinc-300 mx-auto" />
              <h3 className="text-sm font-bold text-zinc-700">Nenhum documento encontrado</h3>
              <p className="text-xs text-zinc-500 max-w-xs mx-auto leading-relaxed">
                Crie propostas e ordens de serviço para coletar assinaturas, ou crie um Novo Termo avulso acima.
              </p>
            </div>
          ) : (
            filteredDocs.map(doc => {
              const isSigned = doc.status === 'signed';
              const docTypeLabel = doc.type === 'work_order_delivery' ? 'Entrega de OS' : 
                                  doc.type === 'quote_approval' ? 'Aceite de Proposta' : 'Termo de Aceite';

              return (
                <div
                  key={doc.id}
                  className={`bg-white border rounded-3xl p-5 shadow-2xs transition-all space-y-4 ${
                    isSigned ? 'border-emerald-200/80' : 'border-zinc-200/80 hover:border-zinc-300'
                  }`}
                >
                  {/* Cabeçalho do Card */}
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <span className={`text-[11px] font-black px-2.5 py-1 rounded-xl uppercase tracking-wider border ${
                        isSigned 
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-200' 
                          : 'bg-amber-50 text-amber-800 border-amber-200'
                      }`}>
                        {doc.code}
                      </span>
                      <h3 className="text-sm font-extrabold text-zinc-900">{doc.title}</h3>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {isSigned ? (
                        <span className="flex items-center gap-1 text-emerald-700 text-xs font-black bg-emerald-50 px-2.5 py-1 rounded-xl border border-emerald-200">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Assinado
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-700 text-xs font-black bg-amber-50 px-2.5 py-1 rounded-xl border border-amber-200">
                          <Clock className="h-3.5 w-3.5" />
                          Pendente
                        </span>
                      )}
                      <strong className="text-sm font-black text-brand">
                        {formatCurrencyBRL(doc.totalValue)}
                      </strong>
                    </div>
                  </div>

                  {/* Informações do Cliente */}
                  <div className="grid sm:grid-cols-3 gap-x-4 gap-y-2 text-xs bg-zinc-50/80 rounded-2xl px-4 py-3 border border-zinc-100">
                    <div>
                      <span className="text-[10px] uppercase font-black text-zinc-400 tracking-widest block mb-0.5">Cliente</span>
                      <strong className="text-sm text-zinc-900 font-bold">{doc.clientName}</strong>
                      {doc.clientPhone && <span className="block text-[11px] text-zinc-500 font-medium mt-0.5">{doc.clientPhone}</span>}
                    </div>

                    <div>
                      <span className="text-[10px] uppercase font-black text-zinc-400 tracking-widest block mb-0.5">Data</span>
                      <span className="text-zinc-700 font-semibold">{doc.createdAt}</span>
                    </div>

                    <div>
                      <span className="text-[10px] uppercase font-black text-zinc-400 tracking-widest block mb-0.5">Tipo</span>
                      <span className="text-zinc-700 font-semibold">{docTypeLabel}</span>
                    </div>
                  </div>

                  {/* Detalhes da Assinatura Forense */}
                  {isSigned && doc.signature && (
                    <div className="px-4 py-3 bg-emerald-50/80 rounded-2xl border border-emerald-100 text-xs flex items-center justify-between gap-3">
                      <div className="flex items-center gap-1.5 text-emerald-900 min-w-0">
                        <CheckCircle2 className="h-4 w-4 text-brand shrink-0" />
                        <span className="font-bold truncate">
                          Assinado por {doc.signature.clientName} • {new Date(doc.signature.signedAt).toLocaleString('pt-BR')}
                        </span>
                      </div>
                      <span className="font-mono text-[9px] bg-white px-2 py-0.5 rounded-lg border border-emerald-200 text-brand font-bold shrink-0">
                        #{doc.signature.verificationHash.substring(0, 8)}
                      </span>
                    </div>
                  )}

                  {/* Ações do Card */}
                  <div className="flex items-center justify-end gap-2">
                    {isSigned ? (
                      <button
                        type="button"
                        onClick={() => setViewingSignedDoc(doc)}
                        className="px-4 py-2.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-2 active:scale-95"
                      >
                        <FileText className="h-4 w-4 text-brand" />
                        Ver Comprovante
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleOpenSign(doc)}
                        className="px-4 py-2.5 bg-brand hover:bg-brand-dark text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-sm shadow-brand/20 transition-all cursor-pointer flex items-center gap-2 active:scale-95"
                      >
                        <PenTool className="h-4 w-4" />
                        Pegar Assinatura Agora
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>


      {/* Modal de Visualização do Documento Assinado */}
      {viewingSignedDoc && (
        <div className="fixed inset-0 z-100 bg-neutral-950/85 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-white rounded-3xl p-6 space-y-4 shadow-2xl border border-zinc-100 text-xs">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 text-brand flex items-center justify-center font-bold">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-zinc-900">{viewingSignedDoc.code} - {viewingSignedDoc.title}</h3>
                  <p className="text-[11px] text-zinc-500 font-medium">Documento com Assinatura Digital Registrada</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setViewingSignedDoc(null)}
                className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded-full cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 bg-zinc-50 p-3 rounded-2xl border border-zinc-100">
                <div>
                  <span className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Cliente</span>
                  <span className="font-bold text-zinc-900">{viewingSignedDoc.clientName}</span>
                </div>
                <div>
                  <span className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Valor</span>
                  <span className="font-black text-brand">{formatCurrencyBRL(viewingSignedDoc.totalValue)}</span>
                </div>
              </div>

              {viewingSignedDoc.signature?.clientSignatureBase64 ? (
                <div className="space-y-1">
                  <span className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Rubrica do Cliente</span>
                  <div className="h-28 bg-zinc-50 border border-zinc-200 rounded-2xl overflow-hidden flex items-center justify-center p-2">
                    <img
                      src={viewingSignedDoc.signature.clientSignatureBase64}
                      alt="Assinatura"
                      className="max-h-full object-contain"
                    />
                  </div>
                </div>
              ) : (
                <div className="py-6 text-center text-zinc-400 bg-zinc-50 rounded-2xl border border-dashed border-zinc-200">
                  Assinatura registrada e concluída no sistema.
                </div>
              )}

              {viewingSignedDoc.signature && (
                <div className="p-3 bg-emerald-50/70 border border-emerald-100 rounded-2xl space-y-1 text-[10px]">
                  <p className="font-bold text-emerald-950 flex items-center gap-1">
                    <CheckCircle2 className="h-3.5 w-3.5 text-brand" />
                    <span>Data/Hora: {new Date(viewingSignedDoc.signature.signedAt).toLocaleString('pt-BR')}</span>
                  </p>
                  <p className="font-mono text-zinc-600 truncate">
                    Hash SHA-256: {viewingSignedDoc.signature.verificationHash}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-2 border-t border-zinc-100">
              <button
                type="button"
                onClick={() => setViewingSignedDoc(null)}
                className="px-5 py-2.5 bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-xs rounded-xl transition-all cursor-pointer"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Assinatura Touchscreen Vinculada ao Documento Selecionado */}
      {selectedDocForSign && (
        <TouchSignatureModal
          isOpen={isSignModalOpen}
          onClose={() => {
            setIsSignModalOpen(false);
            setSelectedDocForSign(null);
          }}
          documentId={selectedDocForSign.code}
          documentType={selectedDocForSign.type}
          documentTitle={selectedDocForSign.title}
          providerName={providerName}
          clientName={selectedDocForSign.clientName}
          clientDocument={selectedDocForSign.clientDocument}
          totalValue={selectedDocForSign.totalValue}
          onSaveSignature={handleSaveSignature}
        />
      )}

      {/* Modal para Criar Termo Avulso Rápido */}
      {isQuickTermModalOpen && (
        <div className="fixed inset-0 z-100 bg-neutral-950/85 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={handleCreateQuickTerm} className="w-full max-w-md bg-white rounded-3xl p-6 space-y-4 shadow-2xl border border-zinc-100 text-xs">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
              <div>
                <h3 className="text-sm font-black text-zinc-900">Novo Termo de Aceite / Assinatura Avulsa</h3>
                <p className="text-zinc-500 font-medium text-[11px]">Crie um termo rápido na hora para coletar a assinatura de um cliente.</p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">Título do Serviço / Objeto *</label>
                <input
                  type="text"
                  required
                  value={quickTitle}
                  onChange={e => setQuickTitle(e.target.value)}
                  placeholder="Ex: Instalação de Ar-Condicionado e Teste de Carga"
                  className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>

              <div>
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">Cliente *</label>
                <input
                  type="text"
                  required
                  value={quickClientName}
                  onChange={e => setQuickClientName(e.target.value)}
                  placeholder="Nome do cliente"
                  className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>

              <div>
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">Valor do Documento (R$)</label>
                <input
                  type="number"
                  value={quickValue}
                  onChange={e => setQuickValue(parseFloat(e.target.value) || 0)}
                  className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-bold focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-100">
              <button
                type="button"
                onClick={() => setIsQuickTermModalOpen(false)}
                className="px-4 py-2.5 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-xl font-bold transition-all cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-sm shadow-brand/20 transition-all cursor-pointer active:scale-95"
              >
                Prosseguir para Assinatura →
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
